import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Ticket, Settings, X, Send } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import {
  helpdesk as api,
  uploads as uploadsApi,
  slaConfigs as slaApi,
  assignmentRules as ruleApi,
  type Ticket as ApiTicket,
  type TicketComment,
  type TicketAttachment,
  type SlaConfig,
  type TicketAssignmentRule,
  type ApiError,
} from '../../services/api';
import DataGrid from '../../components/DataGrid';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

const INIT = { title: '', description: '', category: '', priority: 'medium' };

const priorityColor = (p: string) =>
  p === 'urgent' ? { bg: 'rgba(239,68,68,0.1)', text: '#DC2626' }
    : p === 'high' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
      : p === 'medium' ? { bg: 'rgba(26,108,246,0.1)', text: '#1A6CF6' }
        : { bg: 'rgba(16,185,129,0.1)', text: '#059669' };

const statusColor = (s: string) =>
  s === 'open' ? { bg: 'rgba(239,68,68,0.1)', text: '#DC2626' }
    : s === 'in-progress' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
      : s === 'resolved' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
        : { bg: 'rgba(148,163,184,0.1)', text: '#64748B' };

const CATEGORIES = ['Plumbing', 'Electrical', 'Security', 'Housekeeping', 'Lift', 'Parking', 'Internet', 'Other'];
const POLL_MS = 20000;

const timeAgo = (iso: string) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const HelpdeskPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isAdmin = user?.role === 'SOCIETY_ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'STAFF';

  const [items, setItems] = useState<ApiTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(INIT);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [detailTicket, setDetailTicket] = useState<ApiTicket | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const refresh = useCallback(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!societyId) return;
    const interval = setInterval(refresh, POLL_MS);
    return () => clearInterval(interval);
  }, [societyId, refresh]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(e.target.files ?? []));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    try {
      const created = await api.create(societyId, {
        title: form.title, priority: form.priority as ApiTicket['priority'],
        description: form.description || undefined, category: form.category || undefined,
      });
      for (const file of files) {
        try {
          const { url } = await uploadsApi.upload(societyId, file);
          const fileType = file.type.startsWith('video/') ? 'video' : 'image';
          await api.addAttachment(societyId, created.id, url, fileType);
        } catch {
          /* one failed attachment shouldn't block ticket creation */
        }
      }
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(INIT); setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to raise ticket');
    } finally { setSaving(false); }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Helpdesk
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{items.filter(t => t.status === 'open').length} open · {items.length} total</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button onClick={() => setShowSettings(true)} className="nx-btn nx-btn-outline" style={{ gap: 6 }}>
              <Settings size={14} /> SLA & Assignment
            </button>
          )}
          <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Raise Ticket
          </button>
        </div>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            {
              key: 'title', header: 'Title',
              cellStyle: { fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              render: (t: ApiTicket) => t.title,
            },
            {
              key: 'raisedBy', header: 'Raised By',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              render: (t: ApiTicket) => t.residentName ? (
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--nx-text-primary)' }}>{t.residentName}</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                    {[t.blockName, t.unitNumber].filter(Boolean).join(' · ') || '—'}{t.residentPhone ? ` · ${t.residentPhone}` : ''}
                  </div>
                </div>
              ) : '—',
            },
            {
              key: 'category', header: 'Category',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              render: (t: ApiTicket) => t.category || '—',
            },
            {
              key: 'priority', header: 'Priority',
              render: (t: ApiTicket) => {
                const pc = priorityColor(t.priority);
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: pc.bg, color: pc.text }}>
                    {t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}
                  </span>
                );
              },
            },
            {
              key: 'status', header: 'Status',
              render: (t: ApiTicket) => {
                const sc = statusColor(t.status);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                      {t.status === 'in-progress' ? 'In Progress' : t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                    </span>
                    {t.slaBreached && (
                      <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: 'rgba(239,68,68,0.1)', color: '#DC2626' }}>
                        SLA Breached
                      </span>
                    )}
                  </div>
                );
              },
            },
            {
              key: 'raised', header: 'Raised',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (t: ApiTicket) => timeAgo(t.createdAt),
            },
            {
              key: 'due', header: 'SLA Due',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (t: ApiTicket) => t.dueAt ? new Date(t.dueAt).toLocaleString() : '—',
            },
            {
              key: 'actions', header: '',
              render: (t: ApiTicket) => (
                <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setDetailTicket(t)}>
                  View
                </button>
              ),
            },
          ]}
          rows={items}
          rowKey={t => t.id}
          loading={loading}
          emptyIcon={<Ticket size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No tickets yet. Raise the first one."
        />
      </div>

      {showForm && (
        <Modal
          title="Raise Ticket" submitLabel="Raise Ticket" error={formError} isLoading={saving}
          onClose={() => { setShowForm(false); setForm(INIT); setFiles([]); setFormError(''); }}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Title *</label>
            <input name="title" value={form.title} onChange={change} required placeholder="Lift not working — B Block" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <select name="category" value={form.category} onChange={change} style={inp}>
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c.toLowerCase()}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Priority *</label>
              <select name="priority" value={form.priority} onChange={change} style={inp}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label style={lbl}>Description</label>
            <textarea
              name="description" value={form.description}
              onChange={change} placeholder="Describe the issue in detail…"
              style={{ ...inp, minHeight: 90, resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={lbl}>Photo / Video (optional)</label>
            <input
              ref={fileInputRef} type="file" accept="image/*,video/*" multiple
              onChange={handleFilesChange} style={inp}
            />
            {files.length > 0 && (
              <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>
                {files.length} file{files.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>
        </Modal>
      )}

      {detailTicket && (
        <TicketDetailModal
          ticket={detailTicket}
          societyId={societyId}
          isAdmin={isAdmin}
          onClose={() => setDetailTicket(null)}
          onUpdated={(updated) => {
            setItems(p => p.map(t => t.id === updated.id ? updated : t));
            setDetailTicket(updated);
          }}
        />
      )}

      {showSettings && societyId && (
        <SlaSettingsModal societyId={societyId} onClose={() => setShowSettings(false)} />
      )}
    </PageLayout>
  );
};

const TicketDetailModal: React.FC<{
  ticket: ApiTicket;
  societyId: string | null;
  isAdmin: boolean;
  onClose: () => void;
  onUpdated: (t: ApiTicket) => void;
}> = ({ ticket, societyId, isAdmin, onClose, onUpdated }) => {
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState(ticket.quoteAmount != null ? String(ticket.quoteAmount) : '');
  const [quoteStatus, setQuoteStatus] = useState(ticket.quoteStatus ?? '');

  useEffect(() => {
    if (!societyId) return;
    api.listComments(societyId, ticket.id).then(setComments).catch(() => {});
    api.listAttachments(societyId, ticket.id).then(setAttachments).catch(() => {});
  }, [societyId, ticket.id]);

  const postComment = async () => {
    if (!societyId || !newComment.trim()) return;
    setPosting(true);
    try {
      const created = await api.addComment(societyId, ticket.id, newComment.trim());
      setComments(p => [...p, created]);
      setNewComment('');
    } catch { /* ignore */ } finally { setPosting(false); }
  };

  const updateField = async (patch: Record<string, unknown>) => {
    if (!societyId) return;
    setSaving(true);
    try {
      const updated = await api.update(societyId, ticket.id, patch);
      onUpdated(updated);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(8,14,29,0.75)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'relative', zIndex: 1, width: '100%', maxWidth: 620,
        background: 'var(--nx-surface-1)', borderRadius: 16, border: '1px solid var(--nx-border)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', maxHeight: '88vh',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--nx-border)' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>{ticket.title}</h2>
          <button type="button" onClick={onClose} style={{ width: 28, height: 28, border: 'none', background: 'var(--nx-surface-3)', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nx-text-muted)' }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ fontSize: 13, color: 'var(--nx-text-secondary)' }}>{ticket.description || 'No description provided.'}</p>

          {ticket.residentName && (
            <div style={{ padding: '10px 12px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12, color: 'var(--nx-text-secondary)' }}>
              <strong style={{ color: 'var(--nx-text-primary)' }}>Raised by:</strong> {ticket.residentName}
              {(ticket.blockName || ticket.unitNumber) ? ` · ${[ticket.blockName, ticket.unitNumber].filter(Boolean).join(' · ')}` : ''}
              {ticket.residentPhone ? ` · ${ticket.residentPhone}` : ''}
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--nx-text-muted)', flexWrap: 'wrap' }}>
            <span>Due: {ticket.dueAt ? new Date(ticket.dueAt).toLocaleString() : '—'}</span>
            {ticket.slaBreached && <span style={{ color: '#DC2626', fontWeight: 600 }}>SLA breached — escalated</span>}
          </div>

          {isAdmin && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={lbl}>Status</label>
                <select
                  value={ticket.status} disabled={saving} style={inp}
                  onChange={e => updateField({ status: e.target.value })}
                >
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Priority</label>
                <select
                  value={ticket.priority} disabled={saving} style={inp}
                  onChange={e => updateField({ priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          )}

          {isAdmin && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
              <div>
                <label style={lbl}>Quote Amount (₹)</label>
                <input type="number" value={quoteAmount} onChange={e => setQuoteAmount(e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>Quote Status</label>
                <select value={quoteStatus} onChange={e => setQuoteStatus(e.target.value)} style={inp}>
                  <option value="">—</option>
                  <option value="requested">Requested</option>
                  <option value="received">Received</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <button
                className="nx-btn nx-btn-outline nx-btn-sm" disabled={saving}
                onClick={() => updateField({ quoteAmount: quoteAmount ? Number(quoteAmount) : null, quoteStatus: quoteStatus || null })}
              >
                Save
              </button>
            </div>
          )}

          {attachments.length > 0 && (
            <div>
              <label style={lbl}>Attachments</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {attachments.map(a => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                    {a.fileType === 'video' ? (
                      <video src={a.url} style={{ width: 120, height: 90, borderRadius: 8, objectFit: 'cover' }} controls />
                    ) : (
                      <img src={a.url} alt="attachment" style={{ width: 120, height: 90, borderRadius: 8, objectFit: 'cover' }} />
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <label style={lbl}>Comments</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
              {comments.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No comments yet.</p>
              ) : comments.map(c => (
                <div key={c.id} style={{ padding: '8px 10px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12 }}>
                  <p style={{ color: 'var(--nx-text-primary)' }}>{c.body}</p>
                  <p style={{ color: 'var(--nx-text-muted)', fontSize: 10, marginTop: 4 }}>{timeAgo(c.createdAt)}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={newComment} onChange={e => setNewComment(e.target.value)}
                placeholder="Add a comment…" style={{ ...inp, flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') postComment(); }}
              />
              <button className="nx-btn nx-btn-primary nx-btn-sm" disabled={posting || !newComment.trim()} onClick={postComment}>
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SlaSettingsModal: React.FC<{ societyId: string; onClose: () => void }> = ({ societyId, onClose }) => {
  const [slaList, setSlaList] = useState<SlaConfig[]>([]);
  const [ruleList, setRuleList] = useState<TicketAssignmentRule[]>([]);
  const [slaForm, setSlaForm] = useState({ category: '', priority: 'medium', responseMinutes: '', resolutionMinutes: '' });
  const [ruleForm, setRuleForm] = useState({ category: '', blockId: '', assignToUserId: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    slaApi.list(societyId).then(setSlaList).catch(() => {});
    ruleApi.list(societyId).then(setRuleList).catch(() => {});
  }, [societyId]);

  const addSla = async () => {
    if (!slaForm.resolutionMinutes) return;
    setSaving(true); setError('');
    try {
      const created = await slaApi.create(societyId, {
        category: slaForm.category || undefined,
        priority: slaForm.priority,
        responseMinutes: slaForm.responseMinutes ? Number(slaForm.responseMinutes) : undefined,
        resolutionMinutes: Number(slaForm.resolutionMinutes),
      });
      setSlaList(p => [created, ...p]);
      setSlaForm({ category: '', priority: 'medium', responseMinutes: '', resolutionMinutes: '' });
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add SLA config');
    } finally { setSaving(false); }
  };

  const removeSla = async (id: string) => {
    await slaApi.delete(societyId, id).catch(() => {});
    setSlaList(p => p.filter(s => s.id !== id));
  };

  const addRule = async () => {
    if (!ruleForm.category || !ruleForm.assignToUserId) return;
    setSaving(true); setError('');
    try {
      const created = await ruleApi.create(societyId, {
        category: ruleForm.category,
        blockId: ruleForm.blockId || undefined,
        assignToUserId: ruleForm.assignToUserId,
      });
      setRuleList(p => [created, ...p]);
      setRuleForm({ category: '', blockId: '', assignToUserId: '' });
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add assignment rule');
    } finally { setSaving(false); }
  };

  const removeRule = async (id: string) => {
    await ruleApi.delete(societyId, id).catch(() => {});
    setRuleList(p => p.filter(r => r.id !== id));
  };

  return (
    <Modal title="SLA & Assignment Rules" submitLabel="Done" error={error} onClose={onClose} onSubmit={e => { e.preventDefault(); onClose(); }}>
      <div>
        <label style={lbl}>SLA Configs</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
          <input placeholder="Category (blank=all)" value={slaForm.category} onChange={e => setSlaForm(p => ({ ...p, category: e.target.value }))} style={inp} />
          <select value={slaForm.priority} onChange={e => setSlaForm(p => ({ ...p, priority: e.target.value }))} style={inp}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          <input type="number" placeholder="Response (min)" value={slaForm.responseMinutes} onChange={e => setSlaForm(p => ({ ...p, responseMinutes: e.target.value }))} style={inp} />
          <input type="number" placeholder="Resolution (min) *" value={slaForm.resolutionMinutes} onChange={e => setSlaForm(p => ({ ...p, resolutionMinutes: e.target.value }))} style={inp} />
          <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" disabled={saving} onClick={addSla}>Add</button>
        </div>
        {slaList.map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12, marginBottom: 4 }}>
            <span>{s.category ?? 'Any category'} · {s.priority} · {s.resolutionMinutes}min</span>
            <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)' }} onClick={() => removeSla(s.id)}><X size={13} /></button>
          </div>
        ))}
      </div>

      <div>
        <label style={lbl}>Auto-Assignment Rules</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
          <input placeholder="Category *" value={ruleForm.category} onChange={e => setRuleForm(p => ({ ...p, category: e.target.value }))} style={inp} />
          <input placeholder="Block ID (blank=all)" value={ruleForm.blockId} onChange={e => setRuleForm(p => ({ ...p, blockId: e.target.value }))} style={inp} />
          <input placeholder="Assign to User ID *" value={ruleForm.assignToUserId} onChange={e => setRuleForm(p => ({ ...p, assignToUserId: e.target.value }))} style={inp} />
          <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" disabled={saving} onClick={addRule}>Add</button>
        </div>
        {ruleList.map(r => (
          <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12, marginBottom: 4 }}>
            <span>{r.category} {r.blockId ? `· block ${r.blockId.slice(0, 8)}` : '· any block'} → {r.assignToUserId.slice(0, 8)}</span>
            <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)' }} onClick={() => removeRule(r.id)}><X size={13} /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default HelpdeskPage;
